import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';
import { GlobalMessages } from '@shared/localization/messages';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import CheckCircleIcon from '@/assets/check-circle-icon.svg?react';
import IconInfoCircle from '@/assets/Icons/IconInfoCircle';
import { AnalyticsEvent } from '@/constants/analyticsEvents';
import { useLocalization } from '@/context/localization';
import { useMessages } from '@/hooks/messages';

import { Button } from '../../atoms/Button';
import { RangeFormField } from '../../atoms/RangeFormField';
import { SelectFormField } from '../../atoms/SelectFormField';
import type { ModalStatelessProps } from '../../ModalStateless';
import { ModalStateless } from '../../ModalStateless';
import { aiReviewParamsMessages } from './messages';

type AiReviewParamsModalProps = Omit<
  ModalStatelessProps,
  'title' | 'buttons' | 'withoutButtons' | 'className'
> & {
  estimationId: string;
  estimationCultureName: string;
  onSubmit: (maxWordsCount: number, cultureName: string) => void;
};

// EWM-1156: the viewer only offers tenant languages and the API matches culture exactly, so a
// review requested in a culture the tenant does not support can never be read back. An empty list
// means the tenant has not loaded yet, so the estimation culture is kept rather than overridden.
// The user locale is not guaranteed to be a tenant language either, hence the first-language fallback.
export const getDefaultAiReviewCulture = (
  estimationCultureName: string,
  supportedCultureNames: string[],
  locale: string,
) => {
  const isSupported = (cultureName: string) => supportedCultureNames.includes(cultureName);
  if (supportedCultureNames.length === 0 || isSupported(estimationCultureName)) {
    return estimationCultureName;
  }
  return isSupported(locale) ? locale : supportedCultureNames[0];
};

export const AiReviewParamsModal: React.FC<AiReviewParamsModalProps> = ({
  open,
  estimationId,
  estimationCultureName,
  onClose,
  onSubmit,
}) => {
  const globalMessages = useMessages(GlobalMessages);
  const messages = useMessages(aiReviewParamsMessages);
  const {
    state: { languageList, locale },
  } = useLocalization();
  const appInsights = useAppInsightsContext();
  const trackCreateNewAiReviewRequest = useTrackEvent(
    appInsights,
    AnalyticsEvent.CreateNewAiReviewRequest,
    {},
  );

  const languages = useMemo(
    () => languageList.map((l) => ({ value: l.code, label: l.name })),
    [languageList],
  );

  // keep in sync these 2 consts with the corresponding constants on BE (see StaticSettings.AiIntegration)
  const ManagementSummaryResponseMinWordsCount = 300;
  const ManagementSummaryResponseMaxWordsCount = 2000;

  const DefaultManagementSummaryResponseWordsCount = 1000;

  const defaultCultureName = getDefaultAiReviewCulture(
    estimationCultureName,
    languageList.map((l) => l.code),
    locale,
  );
  // The modal mounts before the tenant languages load, so the default is fed through `values`
  // to re-sync once they arrive; keepDirtyValues leaves a user's own pick alone.
  const values = useMemo(
    () => ({
      maxWordsCount: DefaultManagementSummaryResponseWordsCount,
      cultureName: defaultCultureName,
    }),
    [defaultCultureName],
  );
  const { control, handleSubmit, reset } = useForm<{ maxWordsCount: number; cultureName: string }>({
    values,
    resetOptions: { keepDirtyValues: true },
  });

  const handleSubmitInternal = useCallback(
    (params: { maxWordsCount: number; cultureName: string }) => {
      onSubmit(params.maxWordsCount, params.cultureName);
      trackCreateNewAiReviewRequest({
        estimationId,
        maxWordsCount: params.maxWordsCount,
        cultureName: params.cultureName,
      });
      reset();
    },
    [estimationId, onSubmit, reset, trackCreateNewAiReviewRequest],
  );

  return (
    <ModalStateless
      title={messages.title()}
      size='s'
      open={open}
      onClose={onClose}
      buttons={
        <>
          <Button variant='secondary' outlined onClick={onClose}>
            {globalMessages.cancel()}
          </Button>
          <Button
            variant='primary'
            IconComponentLeft={CheckCircleIcon}
            onClick={() => handleSubmit(handleSubmitInternal)()}
          >
            {messages.createRequest()}
          </Button>
        </>
      }
    >
      <form>
        <div className='text-md mb-4 text-slate-300'>
          <p className='mb-1'>{messages.helpTitle()}</p>
          <div>
            <IconInfoCircle className='mr-1 inline h-4 w-4' />
            <small className='mt-2 inline text-slate-400 italic'>{messages.helpText()}</small>
          </div>
        </div>
        <div className='border-c1 mb-4 border-t-2 pt-4'>
          <label className='form-label'>
            {messages.maxWordsCount()}
            <RangeFormField
              control={control}
              fieldPath='maxWordsCount'
              min={ManagementSummaryResponseMinWordsCount}
              max={ManagementSummaryResponseMaxWordsCount}
              step={50}
              containerClassName='mt-2'
              className='w-full'
            />
          </label>
        </div>
        <div className='custom-select z-[1000] mb-4'>
          <label className='form-label'>
            {messages.language()}
            <SelectFormField
              control={control}
              isClearable={false}
              fieldPath='cultureName'
              options={languages}
              rules={{ required: true }}
              classNames={{ control: () => 'mt-2' }}
            />
          </label>
        </div>
      </form>
    </ModalStateless>
  );
};
